"use client"

import { Calculator, TrendingUp } from 'lucide-react'
import { Button } from "@/components/ui/button"
import { useState } from 'react'
import LeadCaptureModal from '@/components/LeadCaptureModal'

type ProjectFinancingProps = {
  basePrice: number
  governmentBenefits: string | null
  financing: string | null
  hasDownPaymentBonus: boolean
  onPreEvaluate: () => void
  // Unit information props
  unitNumber?: string
  unitBedrooms?: number
  unitTotalSurface?: number
  unitOrientation?: string
  unitFloor?: number
  unitPrice?: {
    uf: number
    clp: number
  }
  paymentConfig?: any
  // Project info for modal
  projectTitle?: string
  projectId?: string
  unitId?: string
}

// Helper function to format orientation for display
const getOrientationDisplay = (orientation: string | undefined) => {
  if (!orientation) return 'N/E'
  
  const orientations: { [key: string]: string } = {
    'north': 'Norte',
    'south': 'Sur', 
    'east': 'Oriente',
    'west': 'Poniente',
    'northeast': 'Noreste',
    'northwest': 'Noroeste', 
    'southeast': 'Sureste',
    'southwest': 'Suroeste',
    'norte': 'Norte',
    'sur': 'Sur',
    'oriente': 'Oriente', 
    'poniente': 'Poniente',
    'noreste': 'Noreste',
    'noroeste': 'Noroeste',
    'sureste': 'Sureste',
    'suroeste': 'Suroeste'
  }
  
  return orientations[orientation.toLowerCase()] || orientation
}

// Helper function to format UF values
const formatUF = (amount: number) => {
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount)
}

export default function ProjectFinancing({
  basePrice,
  governmentBenefits,
  financing,
  hasDownPaymentBonus,
  onPreEvaluate,
  unitNumber,
  unitBedrooms,
  unitTotalSurface,
  unitOrientation,
  unitFloor,
  unitPrice,
  paymentConfig,
  projectTitle,
  projectId,
  unitId
}: ProjectFinancingProps) {
  const [showLeadModal, setShowLeadModal] = useState(false)
  // Calculate discount information
  const getDiscountInfo = () => {
    if (!unitPrice || !paymentConfig?.components) {
      return {
        hasDiscount: false,
        originalPriceUF: unitPrice?.uf || basePrice,
        finalPriceUF: unitPrice?.uf || basePrice,
        discountPercentage: 0
      }
    }
    
    const discountComponent = paymentConfig.components.find((component: any) => 
      component.type === 'descuento' && 'percentage' in component && component.percentage > 0
    )
    
    if (!discountComponent || !('percentage' in discountComponent)) {
      return {
        hasDiscount: false,
        originalPriceUF: unitPrice.uf,
        finalPriceUF: unitPrice.uf,
        discountPercentage: 0
      }
    }
    
    const discountPercentage = discountComponent.percentage
    const originalPriceUF = unitPrice.uf
    const finalPriceUF = originalPriceUF * (1 - discountPercentage / 100)
    
    return {
      hasDiscount: true,
      originalPriceUF,
      finalPriceUF,
      discountPercentage
    }
  }
  
  const discountInfo = getDiscountInfo()

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      
      {/* Unit Information Section */}
      {unitNumber && (
        <div className="mb-4 sm:mb-6 bg-gradient-to-r from-gray-50 to-white rounded-xl p-3 sm:p-4 border border-gray-100">
          <div className="space-y-3 sm:space-y-4">
            {/* Unit Number with Discount Tag */}
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-[#004132] font-inter">
                Departamento {unitNumber}
              </h3>
              {discountInfo.hasDiscount && (
                <span className="bg-[#00C389] text-white text-xs px-2 py-1 rounded-full font-bold">
                  -{discountInfo.discountPercentage.toFixed(1)}% desc.
                </span>
              )}
            </div>
            
            {/* Unit Details - Vertical Stack */}
            <div className="space-y-2 sm:space-y-3">
              {/* Tipología */}
              {unitBedrooms !== undefined && (
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-600 text-sm">Tipología</span>
                  <span className="font-medium text-[#004132]">
                    {unitBedrooms === 0 ? 'Estudio' : `${unitBedrooms} Dormitorio${unitBedrooms !== 1 ? 's' : ''}`}
                  </span>
                </div>
              )}
              
              {/* Área */}
              {unitTotalSurface && (
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-600 text-sm">Área</span>
                  <span className="font-medium text-[#004132]">{unitTotalSurface} m²</span>
                </div>
              )}
              
              {/* Orientación */}
              {unitOrientation && (
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-600 text-sm">Orientación</span>
                  <span className="font-medium text-[#004132] capitalize">{getOrientationDisplay(unitOrientation)}</span>
                </div>
              )}
              
              {/* Piso */}
              {unitFloor && (
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-600 text-sm">Piso</span>
                  <span className="font-medium text-[#004132]">{unitFloor}</span>
                </div>
              )}
              
              {/* Precio */}
              {unitPrice && (
                <>
                  {discountInfo.hasDiscount ? (
                    <>
                      {/* Precio de lista */}
                      <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                        <span className="text-gray-600 text-sm">Precio de lista</span>
                        <span className="font-medium text-[#004132] line-through">
                          {formatUF(discountInfo.originalPriceUF)} UF
                        </span>
                      </div>
                      
                      {/* Precio promocional */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Precio promocional</span>
                        <span className="font-medium text-[#004132]">
                          {formatUF(discountInfo.finalPriceUF)} UF
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Precio</span>
                      <span className="font-medium text-[#004132]">
                        {formatUF(discountInfo.finalPriceUF)} UF
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="mb-6">
        
        {hasDownPaymentBonus && (
          <div className="flex justify-between border-b border-gray-100 pb-2 mb-2">
            <span className="text-gray-600">Bono pie</span>
            <span className="font-medium text-green-600">Disponible</span>
          </div>
        )}
        
        {governmentBenefits && (
          <div className="flex justify-between border-b border-gray-100 pb-2 mb-2">
            <span className="text-gray-600">Subsidios</span>
            <span className="font-medium text-[#004132]">{governmentBenefits}</span>
          </div>
        )}
      </div>
      
      {/* Financing options */}
      {financing && (
        <div className="mb-6">
          <h3 className="font-medium text-[#004132] mb-2">Opciones de financiamiento</h3>
          <p className="text-gray-700 whitespace-pre-line">{financing}</p>
        </div>
      )}
      
      
      {/* CTA Button */}
      <Button 
        onClick={() => setShowLeadModal(true)}
        className="w-full bg-[#FF6B35] hover:bg-[#E55A2B] text-white py-3 font-medium"
        style={{ fontSize: '1.04rem' }}
      >
        Agendar ahora
      </Button>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        projectTitle={projectTitle}
        unitNumber={unitNumber}
        projectId={projectId}
        unitId={unitId}
      />
    </div>
  )
}
